import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type PlanPrices = Record<"free" | "pro" | "premium", { monthly: number | null; yearly: number | null }>;
type PlanKey = keyof PlanPrices;

export interface ActivePromotion {
  percent: number;
  label: string | null;
  start_date: string | null;
  end_date: string | null;
  duration_months: number | null;
}

const FALLBACK: PlanPrices = {
  free: { monthly: 0, yearly: 0 },
  pro: { monthly: 14.9, yearly: 149 },
  premium: { monthly: 24.9, yearly: 249 },
};

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(String(value).replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

export function usePlanPrices() {
  const [prices, setPrices] = useState<PlanPrices>(FALLBACK);
  const [basePrices, setBasePrices] = useState<PlanPrices>(FALLBACK);
  const [promo, setPromo] = useState<ActivePromotion | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      const [{ data: limits }, { data: settingMonthly }, { data: settingYearly }, { data: settingPromo }] = await Promise.all([
        supabase.from("plan_limits").select("plan, monthly_price_brl, yearly_price_brl"),
        supabase.from("app_settings").select("value").eq("key", "plan_prices").maybeSingle(),
        supabase.from("app_settings").select("value").eq("key", "plan_prices_yearly").maybeSingle(),
        supabase.from("app_settings").select("value").eq("key", "promotion").maybeSingle(),
      ]);
      if (!active) return;
      const next: PlanPrices = { ...FALLBACK };
      if (limits && limits.length) {
        for (const row of limits as Array<{ plan: string; monthly_price_brl: number | null; yearly_price_brl: number | null }>) {
          if (row.plan === "free" || row.plan === "pro" || row.plan === "premium") {
            next[row.plan] = {
              monthly: row.monthly_price_brl !== null ? Number(row.monthly_price_brl) : null,
              yearly: row.yearly_price_brl !== null ? Number(row.yearly_price_brl) : null,
            };
          }
        }
      }
      const configuredMonthly = (settingMonthly?.value ?? {}) as Partial<Record<PlanKey, unknown>>;
      const configuredYearly = (settingYearly?.value ?? {}) as Partial<Record<PlanKey, unknown>>;
      (["free", "pro", "premium"] as PlanKey[]).forEach((plan) => {
        if (Object.prototype.hasOwnProperty.call(configuredMonthly, plan)) {
          const monthly = toNumber(configuredMonthly[plan]);
          next[plan] = { ...next[plan], monthly };
        }
        if (Object.prototype.hasOwnProperty.call(configuredYearly, plan)) {
          const yearly = toNumber(configuredYearly[plan]);
          if (yearly !== null) next[plan] = { ...next[plan], yearly };
        } else if (next[plan].yearly == null && next[plan].monthly != null) {
          // Fallback antigo: 10× mensal quando não houver yearly configurado em nenhum lugar
          next[plan] = { ...next[plan], yearly: next[plan].monthly! * 10 };
        }
      });
      setBasePrices(next);

      // Promoção ativa?
      const promoRaw = (settingPromo?.value ?? null) as
        | { enabled?: boolean; percent?: number | null; start_date?: string | null; end_date?: string | null; label?: string | null; duration_months?: number | null }
        | null;
      let activePromo: ActivePromotion | null = null;
      if (promoRaw?.enabled && typeof promoRaw.percent === "number" && promoRaw.percent > 0 && promoRaw.percent < 100) {
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const start = promoRaw.start_date ? new Date(promoRaw.start_date) : null;
        const end = promoRaw.end_date ? new Date(promoRaw.end_date) : null;
        const inRange = (!start || today >= start) && (!end || today <= end);
        if (inRange) {
          activePromo = {
            percent: promoRaw.percent,
            label: promoRaw.label ?? null,
            start_date: promoRaw.start_date ?? null,
            end_date: promoRaw.end_date ?? null,
            duration_months: promoRaw.duration_months ?? null,
          };
        }
      }
      setPromo(activePromo);

      // Aplica desconto sobre tudo (free não tem cobrança)
      const factor = activePromo ? (1 - activePromo.percent / 100) : 1;
      const discounted: PlanPrices = {
        free: next.free,
        pro: {
          monthly: next.pro.monthly != null ? Number((next.pro.monthly * factor).toFixed(2)) : null,
          yearly: next.pro.yearly != null ? Number((next.pro.yearly * factor).toFixed(2)) : null,
        },
        premium: {
          monthly: next.premium.monthly != null ? Number((next.premium.monthly * factor).toFixed(2)) : null,
          yearly: next.premium.yearly != null ? Number((next.premium.yearly * factor).toFixed(2)) : null,
        },
      };
      setPrices(discounted);
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);

  return { prices, basePrices, promotion: promo, loading };
}